import DonutBreakdown from './DonutBreakdown/DonutBreakdown'
import ProgressBreakdown from './ProgressBreakdown/ProgressBreakdown'
import TrendBreakdown from './TrendBreakdown/TrendBreakdown'
import StatCards from './StatCards/StatCards'
import DaySchedule from './DaySchedule/DaySchedule'
import CohortGauges from './CohortGauges/CohortGauges'
import CountList from './CountList/CountList'

const asRows = data => (Array.isArray(data) ? data : data ? [data] : [])

const renderers = {
  DonutBreakdown: {
    Component: DonutBreakdown,
    propsFor: (rows, options) => {
      const {
        labelField = 'label',
        valueField = 'value',
        countField = 'count',
        unit = '',
        size,
        minHeight,
        emptyMessage,
        series,
        countSeries,
        defaultSeries,
      } = options

      return {
        unit,
        ...(size ? { size } : {}),
        ...(minHeight ? { minHeight } : {}),
        ...(emptyMessage ? { emptyMessage } : {}),
        ...(series ? { series } : {}),
        ...(Array.isArray(countSeries) ? { countSeries } : {}),
        ...(defaultSeries ? { defaultSeries } : {}),
        items: asRows(rows).map(row => ({
          label: String(row?.[labelField] ?? ''),
          value: Number(row?.[valueField] ?? 0),
          ...(row?.[countField] == null ? {} : { count: Number(row[countField]) }),
          ...(series && row?.[series] != null ? { [series]: row[series] } : {}),
          ...(row?.color ? { color: row.color } : {}),
        })),
      }
    },
  },

  ProgressBreakdown: {
    Component: ProgressBreakdown,
    propsFor: (rows, options) => {
      const {
        labelField = 'label',
        valueField = 'value',
        totalField,
        overallLabel,
        showOverall = true,
        minHeight,
        emptyMessage,
        sort,
        alertBelow,
        referenceLabel,
        showReference = false,
      } = options

      const list = asRows(rows)

      const items = list.map(row => {
        const value = Number(row?.[valueField] ?? 0)
        const total = totalField ? Number(row?.[totalField] ?? 0) : 100
        return {
          label: String(row?.[labelField] ?? ''),
          percent: total ? Math.round((value / total) * 100) : 0,
          ...(totalField ? { caption: `${value}/${total}` } : {}),
        }
      })

      let overall
      if (showOverall && totalField) {
        const totals = list.reduce(
          (acc, row) => ({
            value: acc.value + Number(row?.[valueField] ?? 0),
            total: acc.total + Number(row?.[totalField] ?? 0),
          }),
          { value: 0, total: 0 },
        )
        overall = totals.total ? Math.round((totals.value / totals.total) * 100) : 0
      }

      return {
        items,
        overall,
        ...(overallLabel ? { overallLabel } : {}),
        ...(minHeight ? { minHeight } : {}),
        ...(emptyMessage ? { emptyMessage } : {}),
        ...(sort ? { sort } : {}),
        ...(alertBelow != null ? { alertBelow } : {}),
        ...(referenceLabel ? { referenceLabel } : {}),
        ...(showReference && overall != null ? { reference: overall } : {}),
      }
    },
  },

  StatCards: {
    Component: StatCards,
    propsFor: (rows, options) => {
      const {
        labelField = 'label',
        valueField = 'value',
        maxField = 'max',
        unitField = 'unit',
        captionField = 'caption',
        detailsField = 'details',
        gaugeLabelField = 'gaugeLabel',
        iconField = 'icon',
        deltaField = 'delta',
        variant,
        columns,
        emptyMessage,
      } = options

      const detailsOf = row => {
        const raw = row?.[detailsField]
        if (Array.isArray(raw)) return raw
        if (typeof raw === 'string') {
          try {
            const parsed = JSON.parse(raw)
            return Array.isArray(parsed) ? parsed : []
          } catch {
            return []
          }
        }
        return []
      }

      return {
        ...(variant ? { variant } : {}),
        ...(columns ? { columns } : {}),
        ...(emptyMessage ? { emptyMessage } : {}),
        cards: asRows(rows).map(row => ({
          label: row?.[labelField],
          value: row?.[valueField],
          max: row?.[maxField],
          unit: row?.[unitField],
          caption: row?.[captionField],
          gaugeLabel: row?.[gaugeLabelField],
          icon: row?.[iconField],
          delta: row?.[deltaField],
          details: detailsOf(row),
        })),
      }
    },
  },

  TrendBreakdown: {
    Component: TrendBreakdown,
    propsFor: (rows, options) => {
      const {
        dateField = 'date',
        valueField = 'value',
        totalField = 'total',
        parts = [],
        periodLabels,
        defaultPeriod,
        control,
        trendDays,
        goodAbove,
        warnAbove,
        minHeight,
        emptyMessage,
      } = options

      return {
        rows: asRows(rows),
        dateField,
        valueField,
        totalField,
        parts,
        ...(periodLabels ? { periodLabels } : {}),
        ...(defaultPeriod ? { defaultPeriod } : {}),
        ...(control ? { control } : {}),
        ...(trendDays ? { trendDays } : {}),
        ...(goodAbove != null ? { goodAbove } : {}),
        ...(warnAbove != null ? { warnAbove } : {}),
        ...(minHeight ? { minHeight } : {}),
        ...(emptyMessage ? { emptyMessage } : {}),
      }
    },
  },

  DaySchedule: {
    Component: DaySchedule,
    propsFor: (rows, options) => {
      const {
        labelField = 'label',
        startField = 'startTime',
        endField = 'endTime',
        roomField = 'room',
        teacherField = 'teachers',
        typeField = 'type',
        minHeight,
        emptyMessage,
      } = options

      const teachersOf = row => {
        const raw = row?.[teacherField]

        if (Array.isArray(raw)) return raw.filter(Boolean).map(String)
        if (typeof raw === 'string' && raw.trim()) return raw.split(/\s*[;|]\s*/).filter(Boolean)

        return []
      }

      return {
        ...(minHeight ? { minHeight } : {}),
        ...(emptyMessage ? { emptyMessage } : {}),
        items: asRows(rows).map(row => ({
          label: String(row?.[labelField] ?? ''),
          startTime: row?.[startField] ?? null,
          endTime: row?.[endField] ?? null,
          room: row?.[roomField] ? String(row[roomField]) : '',
          teachers: teachersOf(row),
          type: row?.[typeField] ?? null,
        })),
      }
    },
  },

  CohortGauges: {
    Component: CohortGauges,
    propsFor: (rows, options) => {
      const {
        labelField = 'label',
        countField = 'count',
        totalField = 'total',
        percentField = 'percent',
        membersField = 'members',
        descriptionField = 'description',
        summaryLabel,
        minHeight,
        emptyMessage,
      } = options


      const membersOf = row => {
        const raw = row?.[membersField]
        const asRecord = value =>
          value && typeof value === 'object' ? value : { name: String(value) }

        if (Array.isArray(raw)) return raw.filter(Boolean).map(asRecord)

        if (typeof raw === 'string' && raw.trim()) {
          const text = raw.trim()

          if (text.startsWith('[')) {
            try {
              const parsed = JSON.parse(text)
              if (Array.isArray(parsed)) return parsed.filter(Boolean).map(asRecord)
            } catch {
              console.warn('Failed to parse members JSON:', text)
            }
          }

          return text.split(/\s*[;|]\s*/).filter(Boolean).map(asRecord)
        }

        return []
      }

      const list = asRows(rows)
      const numeric = value => (value === null || value === undefined || value === '' ? null : Number(value))

      return {
        ...(summaryLabel ? { summaryLabel } : {}),
        ...(minHeight ? { minHeight } : {}),
        ...(emptyMessage ? { emptyMessage } : {}),
        summaryTotal: numeric(list[0]?.[totalField]),
        cohorts: list.map(row => ({
          label: String(row?.[labelField] ?? ''),
          description: row?.[descriptionField] ?? null,
          count: numeric(row?.[countField]),
          total: numeric(row?.[totalField]),
          percent: numeric(row?.[percentField]),
          members: membersOf(row),
        })),
      }
    },
  },

  CountList: {
    Component: CountList,
    propsFor: (rows, options) => {
      const {
        keyField = 'key',
        labelField = 'label',
        countField = 'count',
        oldestField = 'oldest_at',
        linkField = 'link',
        iconField = 'icon',
        viewAllLink,
        alertAfterDays,
        minHeight,
        emptyMessage,
      } = options

      return {
        ...(viewAllLink ? { viewAllLink } : {}),
        ...(alertAfterDays != null ? { alertAfterDays } : {}),
        ...(minHeight ? { minHeight } : {}),
        ...(emptyMessage ? { emptyMessage } : {}),
        items: asRows(rows).map(row => ({
          key: row?.[keyField] ?? null,
          label: String(row?.[labelField] ?? ''),
          count: Number(row?.[countField] ?? 0),
          oldestAt: row?.[oldestField] ?? null,
          link: row?.[linkField] ?? null,
          icon: row?.[iconField] ?? null,
        })),
      }
    },
  },
}

export const rendererFor = component => renderers[component] ?? null

export default renderers
